import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Badge, Field, inputClass } from '@/components/ui';
import { adminSuppliersQuery } from '@/queries';

/**
 * Suppliers. Spec 003 US-4, task T093. Spec 013 for the company details.
 *
 * A supplier holds contact details and a lead time. The lead time is not
 * decoration: it is what turns "we are low" into "order it by Tuesday", and it
 * is the only number here anybody has to think about.
 *
 * Each row opens the supplier page: its details, its contacts and the items it
 * sells. Changing, switching off and removing a supplier are there (T520).
 *
 * `company_id` is set from the supplier and never typed on a purchase order.
 * A supplier invoices a legal entity, and an order raised against the wrong one
 * is a tax problem rather than a display problem (G-6, FR-610).
 */
export default function AdminSuppliersPage() {
  const qc = useQueryClient();
  const { suppliers } = useSuspenseQuery(adminSuppliersQuery).data;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [poEmail, setPoEmail] = useState('');
  const [generalEmail, setGeneralEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [leadDays, setLeadDays] = useState('7');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/suppliers', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name, code, po_email: poEmail, general_email: generalEmail, phone,
          lead_days: Number(leadDays) || 7,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null); setName(''); setCode(''); setPoEmail(''); setGeneralEmail('');
      setPhone(''); setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'suppliers'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Suppliers</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Who you buy from, and how long they take.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold"
        >
          {open ? 'Close' : 'Add a supplier'}
        </button>
      </div>

      {open && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}
          className="mb-6 rounded-[10px] border border-line bg-panel p-4"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Name" htmlFor="new-sup-name">
              <input id="new-sup-name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Supplier code" htmlFor="new-sup-code" optional>
              <input id="new-sup-code" className={inputClass} value={code} maxLength={20} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Field label="Lead time in days" htmlFor="new-sup-lead">
              <input
                id="new-sup-lead" className={inputClass} type="number" min="0" value={leadDays}
                onChange={(e) => setLeadDays(e.target.value)}
              />
            </Field>
            <Field label="Purchase order email" htmlFor="new-sup-po-email" optional>
              <input id="new-sup-po-email" className={inputClass} type="email" value={poEmail} onChange={(e) => setPoEmail(e.target.value)} />
            </Field>
            <Field label="General email" htmlFor="new-sup-general-email" optional>
              <input id="new-sup-general-email" className={inputClass} type="email" value={generalEmail} onChange={(e) => setGeneralEmail(e.target.value)} />
            </Field>
            <Field label="Main phone" htmlFor="new-sup-phone" optional>
              <input id="new-sup-phone" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
          </div>
          <p className="mt-3 text-[12.5px] text-muted">
            Add the address, the contacts and the items on the supplier page after you save.
          </p>
          {error && (
            <p className="mt-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>
          )}
          <button
            type="submit"
            disabled={!name.trim() || create.isPending}
            className="mt-4 rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
          >
            {create.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}

      {suppliers.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">No suppliers yet.</p>
          <p className="mx-auto mt-2 max-w-[48ch] text-[14px] text-muted">
            Add the ones you order blanks, thread and film from. The lead time is
            what turns a low stock warning into a date to order by.
          </p>
        </div>
      ) : (
        <div className="rounded-[10px] border border-line bg-panel">
          {suppliers.map((s) => (
            <Link
              key={s.id}
              to="/admin/suppliers/$supplierId"
              params={{ supplierId: s.id }}
              className="flex flex-wrap items-center justify-between gap-3 border-b
                         border-line px-4 py-3 last:border-0 hover:bg-raised"
            >
              <span className="flex flex-col">
                <span className="font-semibold">
                  {s.name}
                  {s.code && <span className="ml-2 font-mono text-[13px] font-normal text-muted">{s.code}</span>}
                  {' '}{!s.active && <Badge tone="warn">switched off</Badge>}
                </span>
                <span className="text-[13px] text-muted">
                  {s.po_email ?? s.general_email ?? 'no email on file'}
                  {s.phone && ` · ${s.phone}`}
                </span>
              </span>
              <span className="text-[14px] text-muted">
                {s.lead_days} {s.lead_days === 1 ? 'day' : 'days'} lead time
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
